import {
  Alert,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";

import { RobotoTableCell, RobotoTableRow } from "@/shared/components";
import { CollectionRecord } from "@/shared/domain/collections";

import { CollectionRow } from "./CollectionRow";

interface CollectionsTableProps {
  collections: CollectionRecord[];
  isLoading: boolean;
}

/**
 * A table of collections.
 */
export default function CollectionsTable({
  collections,
  isLoading,
}: CollectionsTableProps) {
  const theme = useTheme();

  let content: JSX.Element[] | JSX.Element | null = null;
  if (isLoading) {
    content = Array.from({ length: 3 }, (_, rowIdx) => (
      <TableRow key={`collection-row-${rowIdx}`}>
        {Array.from({ length: 7 }, (_, cellIdx) => (
          <RobotoTableCell key={`collection-cell-${cellIdx}`}>
            <Skeleton
              animation="wave"
              variant="rounded"
              height={30}
              sx={{ mt: 1, mb: 1 }}
            />
          </RobotoTableCell>
        ))}
      </TableRow>
    ));
  } else if (collections.length === 0) {
    content = (
      <RobotoTableRow>
        <RobotoTableCell
          style={{
            paddingBottom: theme.spacing(2),
            paddingTop: theme.spacing(2),
          }}
          colSpan={7}
        >
          <Alert severity="warning">
            No collections found. Create one using the button above and then add
            datasets or files to it.
          </Alert>
        </RobotoTableCell>
      </RobotoTableRow>
    );
  } else {
    content = collections.map((collection) => {
      return (
        <CollectionRow
          key={`${collection.org_id}/${collection.collection_id}`}
          collection={collection}
        />
      );
    });
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <RobotoTableCell>Name</RobotoTableCell>
            <RobotoTableCell>ID</RobotoTableCell>
            <RobotoTableCell>Items</RobotoTableCell>
            <RobotoTableCell>Created</RobotoTableCell>
            <RobotoTableCell>Updated By</RobotoTableCell>
            <RobotoTableCell>Updated</RobotoTableCell>
            <RobotoTableCell>Description</RobotoTableCell>
          </TableRow>
        </TableHead>
        <TableBody>{content}</TableBody>
      </Table>
    </TableContainer>
  );
}
